import {
  CloudActions,
  CloudErrors,
  CloudUtils,
  CostCenterServiceUrls,
} from '@portal/portal-core';
import * as FjPrimitives from 'components/primitives';
import { AuthClientUtils } from 'ducks/auth-client';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { APPLICATION_NAME, composeValidators, getCountryOptions, navigate } from 'utils';
import { processPhantomInviteCustomFields } from '../../../ducks/custom-fields/actions';
import { sendAction, sortFieldsUserForm } from '../../../ducks/custom-fields/utils';
import { alertsSelector, selectUserCustomFieldValuesResult } from '../../../selectors';
import {
  validateFirstName,
  validateLastName,
  validateMaxLength,
  validateMinLength,
} from '../../../utils/validators';
import CustomFieldsForm from '../../custom-fields-form';
import { Button, Select } from '../../styled-primitives';
import AuthContainer from '../auth-container';
import { emailPrevalidation } from './pre-validators';
import { correctBadUriHtmlEncoding } from './string-character-correction';

import { serverUrl } from '../../../Config';

const { getAuthToken } = AuthClientUtils;

// Query params are dynamic external input; cloud-core types them loosely.
type QueryParams = Record<string, unknown>;

// Only these fields are updated through the shared onValueChange input handler.
type StringFieldKey =
  | 'primaryEmail'
  | 'confirmPrimaryEmail'
  | 'firstname'
  | 'lastname'
  | 'countryCode'
  | 'password'
  | 'confirmPassword';

interface CreateUserState {
  productName: string;
  primaryEmail: string;
  confirmPrimaryEmail: string;
  firstname: string;
  lastname: string;
  countryCode: string;
  emailVerificationToken: string;
  token: string;
  phantom: string | boolean;
  userId: string;
  password: string;
  confirmPassword: string;
  costCenterId: string;
  customFieldKeys: Record<string, unknown> | unknown[];
  productAccessId: string;
  inviteId: string;
  displayCustomFieldsForm: boolean;
  errorDetail: string;
  costCenterName?: string;
}

// Shape of the invite/cost-center payload returned by the server (boundary — cast from unknown).
interface ProductAccessEntry {
  id: string;
  name: string;
  customFieldKeys?: Parameters<typeof sortFieldsUserForm>[0];
}

interface CostCenterData {
  name: string;
  productAccess: ProductAccessEntry[];
}

interface InviteData {
  productAccessId: string;
  costCenterId: string;
}

interface InviteCostCenterResponse {
  costCenter: CostCenterData;
  invite: InviteData;
}

interface CustomFieldValuesResult {
  errorDetail?: string;
}

type ConvertPhantomDispatch = (
  ...args: Parameters<typeof processPhantomInviteCustomFields>
) => void;

// composeValidators comes from the untyped utils/index.js barrel; its return infers as {}.
// Describe the shape actually consumed by FjInput (a validator + a message getter).
interface ComposedValidator {
  validator: (val: string) => boolean;
  getValidationMessage: (val?: string) => string;
}

// composeValidators is typed for validators whose `validator` takes `unknown`; the validators in
// utils/validators declare a `string` param, which is contravariantly incompatible. Re-type the
// function locally to the (looser) ComposedValidator shape these call sites actually use.
const compose = composeValidators as unknown as (
  ...validators: ComposedValidator[]
) => ComposedValidator;

type CustomFieldsFormPropsType = React.ComponentProps<typeof CustomFieldsForm>;

// FjSelect forwards a DOM `id` through styled-components but omits it from its prop
// type; alias to a props type that includes it so the existing `id` prop is preserved.
const SelectWithId = Select as React.ComponentType<
  React.ComponentProps<typeof Select> & { id?: string }
>;

const FormInput = styled(FjPrimitives.FjInput).attrs(({ theme }: { theme: DefaultTheme }) => ({
  borderColor: theme.colors.selectableLightBg,
  height: 40,
}))`
  width: 300px;
  font-size: ${({ theme }) => theme.fontSizes.p};
`;

const Form = styled.form`
  width: 300px;

  > * {
    margin-bottom: 5px;
  }
`;

const BottomContainer = styled.div`
  display: flex;
  margin: auto;
  align-items: center;
  flex-direction: column;
  padding-bottom: 10px;
`;

const SubmitButton = styled(Button).attrs({
  id: 'submit-button',
  buttonType: FjPrimitives.fjButtonTypes.LG,
})`
  margin-top: 20px;
  margin-bottom: 10px;
`;

// THIS IS A ONE-OFF FIX THAT CAN BE DEPRECATED IN THE FUTURE ONCE THE INVITES IN THE WILD EXPIRE
const stripBadCharsFromToken = (token: string | null | undefined): string | null => {
  if (!token) {
    return null;
  }
  // check if the token has the known bad character signature
  if (token.includes('%0D%20') || token.includes('%20%20')) {
    // if it does, strip those characters from the base64 and then return the remaining string
    const strip1 = token.replace(/%0D/g, '');
    const strip2 = strip1.replace(/%20/g, '');
    return strip2;
  }
  return token;
};

const getParam = (
  params: QueryParams,
  param: string,
  val: string | boolean = '',
): string | boolean =>
  (params[param] as string) || (params[param.toLowerCase()] as string) || val;

const getParams = (
  params: QueryParams,
  defaults: Record<string, string | boolean>,
): Record<string, string | boolean> =>
  Object.keys(defaults).reduce<Record<string, string | boolean>>(
    (acc, param) => ({
      ...acc,
      [param]: getParam(params, param, defaults[param]),
    }),
    {},
  );

const buildInitialState = (params: QueryParams): CreateUserState => {
  const parsed = getParams(params, {
    productName: '',
    primaryEmail: '',
    firstname: '',
    lastname: '',
    countryCode: '',
    emailVerificationToken: '',
    token: '',
    phantom: false,
    userId: '',
  });

  return {
    // parsed values originate from query params; the string fields are known to be strings.
    productName: parsed.productName as string,
    countryCode: parsed.countryCode as string,
    emailVerificationToken: parsed.emailVerificationToken as string,
    token: parsed.token as string,
    userId: parsed.userId as string,
    phantom: parsed.phantom,
    primaryEmail: correctBadUriHtmlEncoding(parsed.primaryEmail as string),
    confirmPrimaryEmail: correctBadUriHtmlEncoding(parsed.primaryEmail as string),
    firstname: correctBadUriHtmlEncoding(parsed.firstname as string),
    lastname: correctBadUriHtmlEncoding(parsed.lastname as string),
    password: '',
    confirmPassword: '',
    costCenterId: '',
    customFieldKeys: {},
    productAccessId: '',
    inviteId: '',
    displayCustomFieldsForm: false,
    errorDetail: '',
  };
};

const CreateUser = (): React.JSX.Element => {
  const alertState = useAppSelector(alertsSelector) as Record<string, unknown>;
  const userCustomFieldValuesResult = useAppSelector(selectUserCustomFieldValuesResult) as
    | CustomFieldValuesResult
    | undefined;
  const dispatch = useAppDispatch();

  const params = useMemo<QueryParams>(
    () => CloudUtils.getCurrentQueryParams() as QueryParams,
    [],
  );
  const [state, setState] = useState<CreateUserState>(() => buildInitialState(params));

  const convertPhantomUserToUserCustomFields: ConvertPhantomDispatch = (...args) =>
    dispatch(processPhantomInviteCustomFields(...args));

  // Mirrors the original componentDidMount: fetch invite-driven custom fields once on mount.
  // The effect callback stays synchronous (it kicks off an inner async fn) and is guarded
  // against setting state after unmount.
  useEffect(() => {
    let cancelled = false;
    const checkForCustomFieldsData = async (): Promise<void> => {
      const { userId, token } = state;
      if (token && userId) {
        const inviteToken = stripBadCharsFromToken(token);
        const serverAction = CloudActions.inviteUserProductGetCostCenter(userId, inviteToken);
        const url = CostCenterServiceUrls.invitePublicUrl(serverUrl);
        const timeout = 5000;
        try {
          const config = {
            getAuthToken: () => getAuthToken(),
          };
          const result = await sendAction(config, serverAction, url, timeout);
          const succeeded = 'succeeded' in result ? result.succeeded : false;
          const data = 'data' in result ? result.data : undefined;
          if (succeeded && data) {
            const { costCenter, invite } = data as InviteCostCenterResponse;
            const { productAccessId, costCenterId } = invite;
            const { productAccess } = costCenter;
            const filtered = productAccess.filter(
              (product) => product.id === productAccessId && product.customFieldKeys,
            );
            const { customFieldKeys, name } = filtered[0];
            if (filtered && customFieldKeys && !cancelled) {
              setState((prev) => ({
                ...prev,
                costCenterName: costCenter.name,
                productName: name,
                costCenterId,
                productAccessId,
                customFieldKeys: sortFieldsUserForm(customFieldKeys),
              }));
            }
          } else if (!cancelled) {
            const errorDetail = ('errorDetail' in result ? result.errorDetail : undefined) as string;
            setState((prev) => ({ ...prev, errorDetail }));
          }
        } catch (error) {
          /* eslint-disable-next-line */
          console.log('error', error);
        }
      }
    };
    checkForCustomFieldsData();
    return () => {
      cancelled = true;
    };
    // run once on mount, mirroring the original componentDidMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onValueChange = (key: StringFieldKey, value: string): void =>
    setState((prev) => ({ ...prev, [key]: value }));

  const handleNavigateToPath = (path: string): void => navigate(path);

  const createNewUser = (ev: React.MouseEvent): void => {
    ev.preventDefault();
    ev.stopPropagation();

    const {
      primaryEmail,
      confirmPrimaryEmail,
      firstname,
      lastname,
      countryCode,
      password,
      confirmPassword,
      productName,
      phantom,
      token,
      emailVerificationToken,
      customFieldKeys,
    } = state;

    if (primaryEmail !== confirmPrimaryEmail || password !== confirmPassword) {
      return;
    }

    if (phantom && !Object.keys(customFieldKeys).length) {
      dispatch(
        CloudActions.convertPhantomUserToUser({
          primaryEmail,
          firstname,
          lastname,
          password,
          countryCode,
          token: stripBadCharsFromToken(emailVerificationToken) || stripBadCharsFromToken(token),
          productName,
          tokenType: emailVerificationToken ? 'emailVerification' : 'invite',
        }),
      );
    } else {
      dispatch(
        CloudActions.createNewUser(primaryEmail, firstname, lastname, password, countryCode),
      );
    }
  };

  const renderPrimaryEmail = (disabled: boolean): React.JSX.Element => {
    const emailValidityAlerts = emailPrevalidation(state.primaryEmail);
    const validator = (val: string): boolean =>
      Boolean(val && val.length > 6 && !emailValidityAlerts.length);
    return (
      <FormInput
        id="primary-email"
        type="email"
        placeholder="email"
        autoComplete="email"
        value={state.primaryEmail}
        onChange={(val) => onValueChange('primaryEmail', val)}
        validator={validator}
        getValidationMessage={() => 'Enter a valid email address.'}
        disabled={disabled}
      />
    );
  };

  const renderConfirmPrimaryEmail = (disabled: boolean): React.JSX.Element => {
    const validator = (val: string): boolean =>
      Boolean(val && val.length > 6 && val === state.primaryEmail);
    return (
      <FormInput
        id="confirm-primary-email"
        type="email"
        placeholder="confirm email"
        autoComplete="email"
        value={state.confirmPrimaryEmail}
        onChange={(val) => onValueChange('confirmPrimaryEmail', val)}
        validator={validator}
        getValidationMessage={() => 'Entered emails must match.'}
        disabled={disabled}
      />
    );
  };

  const renderFirstname = (): React.JSX.Element => {
    const { validator, getValidationMessage } = compose(validateFirstName, validateMaxLength(35));
    return (
      <FormInput
        id="firstname"
        placeholder="first name"
        autoComplete="given-name"
        value={state.firstname}
        onChange={(val) => onValueChange('firstname', val)}
        validator={validator}
        getValidationMessage={getValidationMessage}
      />
    );
  };

  const renderLastname = (): React.JSX.Element => {
    const { validator, getValidationMessage } = compose(validateLastName, validateMaxLength(35));
    return (
      <FormInput
        id="lastname"
        placeholder="last name"
        autoComplete="family-name"
        value={state.lastname}
        onChange={(val) => onValueChange('lastname', val)}
        validator={validator}
        getValidationMessage={getValidationMessage}
      />
    );
  };

  const renderCountry = (): React.JSX.Element => (
    <SelectWithId
      id="country"
      autoComplete="country"
      options={[{ value: '', label: 'Please select a country' }].concat(getCountryOptions())}
      value={state.countryCode}
      onChange={(val) => onValueChange('countryCode', val!)}
      keyExtractor={(value, i) => `${value}-${i}`}
      type={FjPrimitives.fjSelectTypes.FORM}
      width={FjPrimitives.fjSelectWidths.WIDE}
    />
  );

  const renderPassword = (): React.JSX.Element => {
    const { validator, getValidationMessage } = compose(validateMinLength(8), validateMaxLength(256));
    return (
      <FormInput
        id="password"
        type="password"
        placeholder="password"
        autoComplete="new-password"
        value={state.password}
        onChange={(val) => onValueChange('password', val)}
        validator={validator}
        getValidationMessage={getValidationMessage}
      />
    );
  };

  const renderConfirmPassword = (): React.JSX.Element => {
    const { validator, getValidationMessage } = compose(
      validateMinLength(8),
      validateMaxLength(256),
      {
        validator: (val: string) => val === state.password,
        getValidationMessage: () => 'Passwords must match.',
      },
    );
    return (
      <FormInput
        id="confirm-password"
        type="password"
        placeholder="verify password"
        autoComplete="new-password"
        value={state.confirmPassword}
        onChange={(val) => onValueChange('confirmPassword', val)}
        validator={validator}
        getValidationMessage={getValidationMessage}
      />
    );
  };

  const renderAlerts = (): React.JSX.Element | null => {
    if (alertState && alertState[CloudErrors.cloudErrorTypes.CANNOT_CREATE_USER]) {
      return (
        <div data-test="already-exists-alert" className="fj-error-container margin-bottom-1">
          <span className="fj-prevalidation">
            Could not create a new account with the submitted data. If all inputs are valid, a user
            account with your email address may already exist.
          </span>
        </div>
      );
    }
    return null;
  };

  const displayCustomFields = (ev: React.MouseEvent): void => {
    ev.preventDefault();
    ev.stopPropagation();
    setState((prev) => ({ ...prev, displayCustomFieldsForm: true }));
  };

  const renderSubmitButton = (): React.JSX.Element => {
    const {
      primaryEmail,
      confirmPrimaryEmail,
      password,
      confirmPassword,
      firstname,
      countryCode,
      customFieldKeys,
    } = state;
    const invalid =
      !primaryEmail ||
      !confirmPrimaryEmail ||
      primaryEmail.length < 6 ||
      primaryEmail.length > 254 ||
      primaryEmail !== confirmPrimaryEmail ||
      !password ||
      !confirmPassword ||
      password.length < 8 ||
      password.length > 256 ||
      password !== confirmPassword ||
      !firstname ||
      !countryCode;
    if (Object.keys(customFieldKeys).length) {
      return (
        <SubmitButton onClick={(ev) => displayCustomFields(ev)} disabled={invalid}>
          Submit
        </SubmitButton>
      );
    }
    return (
      <SubmitButton onClick={createNewUser} disabled={invalid}>
        Submit New User
      </SubmitButton>
    );
  };

  const renderSignInLink = (): React.JSX.Element => (
    <Button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleNavigateToPath('sign-in');
      }}
      buttonType={FjPrimitives.fjButtonTypes.LINK}
    >
      Already have an account? Sign in.
    </Button>
  );

  const renderAccountCreationForm = (): React.JSX.Element => {
    // check query params to see if this is a phantom user
    // if so, disable the email inputs to prevent a user from changing it
    const disabled = !!(params && params.phantom && params.primaryEmail);
    return (
      <Form>
        {renderPrimaryEmail(disabled)}
        {renderConfirmPrimaryEmail(disabled)}
        {renderFirstname()}
        {renderLastname()}
        {renderCountry()}
        {renderPassword()}
        {renderConfirmPassword()}
        {renderAlerts()}
        <BottomContainer>
          {renderSubmitButton()}
          {renderSignInLink()}
        </BottomContainer>
      </Form>
    );
  };

  const onSubmitCustomFields = (
    fields: unknown,
    localState: CreateUserState,
    dispatchAction: ConvertPhantomDispatch,
  ): void => {
    const {
      productName,
      primaryEmail,
      firstname,
      lastname,
      password,
      countryCode,
      token,
      emailVerificationToken,
      userId,
      productAccessId,
      costCenterId,
    } = localState;

    const phantomToUserValues = {
      primaryEmail,
      firstname,
      lastname,
      password,
      countryCode,
      token: stripBadCharsFromToken(emailVerificationToken) || stripBadCharsFromToken(token),
      tokenType: emailVerificationToken ? 'emailVerification' : 'invite',
    };

    dispatchAction(productName, phantomToUserValues, userId, costCenterId, productAccessId, fields);
  };

  const renderCustomFieldsForm = (): React.JSX.Element => {
    const { customFieldKeys, userId, costCenterId, productAccessId, costCenterName, productName } =
      state;

    return (
      <CustomFieldsForm
        userId={userId}
        costCenterId={costCenterId}
        productAccessId={productAccessId}
        customFields={customFieldKeys as CustomFieldsFormPropsType['customFields']}
        mode="live"
        customFieldsAction={onSubmitCustomFields as CustomFieldsFormPropsType['customFieldsAction']}
        costCenterName={costCenterName}
        productName={productName}
        phantom
        parentLocalState={state}
        convertPhantomUserToUserCustomFields={convertPhantomUserToUserCustomFields}
      />
    );
  };

  const { phantom, displayCustomFieldsForm, errorDetail } = state;
  if (errorDetail) {
    return (
      <AuthContainer title="Error">
        The following error was returned by the server: {errorDetail}
        <p>
          Your {APPLICATION_NAME} account may already exist or your product invitation may have been
          canceled.
        </p>
        <BottomContainer>{renderSignInLink()}</BottomContainer>
      </AuthContainer>
    );
  }
  if (userCustomFieldValuesResult && userCustomFieldValuesResult.errorDetail) {
    return (
      <AuthContainer title="Error">
        The following error was returned by the server: {userCustomFieldValuesResult.errorDetail}
        <p>
          Your {APPLICATION_NAME} account may already exist or your product invitation may have been
          canceled.
        </p>
        <BottomContainer>{renderSignInLink()}</BottomContainer>
      </AuthContainer>
    );
  }
  if (phantom && displayCustomFieldsForm && !userCustomFieldValuesResult) {
    return <div>{renderCustomFieldsForm()}</div>;
  }
  return (
    <AuthContainer title={`Create a ${APPLICATION_NAME} Account`}>
      {renderAccountCreationForm()}
    </AuthContainer>
  );
};

export default CreateUser;
